import type { NavigateFunction, NavigateOptions } from 'react-router-dom';
import { useNavigate as useNavigateReactRouter } from 'react-router-dom';

import type { PathParameters, RouteWithChildren } from '../../types';
import hydrateUrlParametersFactory from '../../utils/hydrateUrlParameters/hydrateUrlParametersFactory';

/**
 * This type is used to work out the second parameter of the navigate function return from the useNavigate hook.
 * If the input Path has no URL parameters (ie. '/path/to/something')
 *      the second argument should be optional and only contain the navigation options property
 *      (path: Path, options?: { navigateOptions: NavigateOptions }) => void
 *
 * If the input Path has paramters in the URL (ie. '/path/to/{something}')
 *      the second argument is required and the values for the parameter need to be provided
 *      (path: Path, options: { pathParameters: { something: string }, navigateOptions?: NavigateOptions }) => void
 */
export type TypeConstrainedParameterList<Path extends string> = keyof {
    [ParameterName in PathParameters<Path>]: string;
} extends never
    ? [options?: { navigateOptions: NavigateOptions }]
    : [
          params: {
              pathParameters: { [ParameterName in PathParameters<Path>]: string };
              navigateOptions?: NavigateOptions;
          },
      ];

const hasPathParameters = <Path extends string>(
    options: unknown | undefined,
): options is {
    pathParameters: { [ParameterName in PathParameters<Path>]: string };
    navigateOptions?: NavigateOptions;
} => (options as { pathParameters: object } | undefined)?.pathParameters != null;

const createTypesafeNavigateFn =
    <Routes extends string>(navigateFn: NavigateFunction) =>
    <Path extends RouteWithChildren<'', Routes>>(path: Path, ...args: TypeConstrainedParameterList<Path>) => {
        const [params] = args;
        const hydrateUrlParameters = hydrateUrlParametersFactory<Routes>() as (
            path: Path,
            params: { [ParameterName in PathParameters<Path>]: string },
        ) => string;

        const url = hasPathParameters(params) ? hydrateUrlParameters(path, params.pathParameters) : path;

        navigateFn(url, params?.navigateOptions);
    };

const useNavigate = <Path extends string>() => {
    const navigate = useNavigateReactRouter();

    return createTypesafeNavigateFn<Path>(navigate);
};

export type UseNavigateHook<Path extends string> = typeof useNavigate<Path>;

export default useNavigate;
